import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useId } from "react";
import classNames from 'classnames';
import { getInputSizeStyles, getInputStateClass } from './utils';
/**
 * @export
 * A group of radio buttons for picking exactly one of a few options. Each option can carry a line
 * of help text under its label.
 *
 * Use it when there are two to five choices and the user should see them all at once.
 * For a longer list use `Select`.
 *
 * @example
 * ```
 * <RadioGroup
 *     options={[
 *         { label: 'One file', value: 'one', description: 'All dashboards in a single file.' },
 *         { label: 'A file per dashboard', value: 'each', description: 'One download for each dashboard.' },
 *     ]}
 *     selected={files}
 *     onChange={(value) => setFiles(String(value))}
 * />
 * ```
 *
 * @example
 * ```
 * <RadioGroup
 *     direction="horizontal"
 *     options={[{ label: 'Day', value: 'day' }, { label: 'Week', value: 'week' }, { label: 'Month', value: 'month' }]}
 *     selected={period}
 *     onChange={(value) => setPeriod(String(value))}
 * />
 * ```
 *
 * @category Inputs/Selection
 */
const RadioGroup = (props) => {
    const { options, selected, onChange, direction = "vertical", name, className, isValid, readOnly, disabled, minWidth, maxWidth, width, fullWidth } = props;
    // native radios need one shared name to act as a group (one tab stop, arrow keys move)
    const generatedName = useId();
    const groupName = name || generatedName;
    const sizeStyles = getInputSizeStyles({ minWidth, maxWidth, width, fullWidth, style: undefined });
    const stateClass = getInputStateClass({ disabled, readOnly, isValid });
    return (_jsx("div", { className: classNames("uxpcore_input", "uxpcore_radiogroup-wrapper", stateClass, { "uxpcore_input--inline": true }), style: sizeStyles, children: _jsx("div", { role: "radiogroup", className: classNames("uxpcore_radiogroup", className, {
                "uxpcore_radiogroup--horizontal": direction === "horizontal",
                "uxpcore_radiogroup--disabled": disabled,
                "uxpcore_radiogroup--readonly": readOnly,
                "uxpcore_radiogroup--invalid": isValid === false,
            }), children: (options || []).map((option) => {
                const isSelected = selected !== null && selected !== undefined && String(selected) === String(option.value);
                const isDisabled = disabled || option.disabled;
                return (_jsxs("label", { className: classNames("uxpcore_radiogroup__option", {
                        "uxpcore_radiogroup__option--selected": isSelected,
                        "uxpcore_radiogroup__option--disabled": isDisabled,
                    }), children: [_jsx("input", { type: "radio", className: "uxpcore_radiogroup__input", name: groupName, value: String(option.value), checked: isSelected, disabled: isDisabled, onChange: () => { if (!readOnly && !isDisabled)
                                onChange(option.value); } }), _jsx("div", { className: "uxpcore_radiogroup__indicator" }), _jsxs("div", { className: "uxpcore_radiogroup__content", children: [_jsx("div", { className: "uxpcore_radiogroup__label", children: option.label }), option.description && (_jsx("div", { className: "uxpcore_radiogroup__description", children: option.description }))] })] }, String(option.value)));
            }) }) }));
};
export default RadioGroup;
